import {
  DEBOUNCE_WAIT_FOR_SCROLL,
  DEFAULT_PRIMARY_BATCH_SIZE,
  DEFAULT_ERROR_TITLE,
} from '../../constants/statics'
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import { Icon, withApplicationNotificationsContext } from '@adc/ui-core'
import classnames from 'classnames/bind'
import logger from '@adc/logger'
import DataTable from '../dataTable'
import DataTableUtils from '../dataTable/utils'
import PersistentBrowserStorage from '../../utils/persistentBrowserStorage'
import styles from './styles.scss'
const cx = classnames.bind(styles)

const dataTableLogger = logger.forModule('DataTable')

class DataTableWrapper extends Component {
  static propTypes = {
    columns: PropTypes.array.isRequired,
    cookies: PropTypes.object, // temporary until we stop using it
    entityKey: PropTypes.string.isRequired,
    primaryKeyField: PropTypes.string.isRequired,
    isLoading: PropTypes.bool,
    errorInfo: PropTypes.object,
    rowData: PropTypes.array.isRequired,
    fetchDataRows: PropTypes.func.isRequired, // eslint-disable-line
    fetchDataRowsAppend: PropTypes.func.isRequired, // eslint-disable-line
    filterClauses: PropTypes.array.isRequired,
    totalCount: PropTypes.number,
    addFilter: PropTypes.func,
    removeFilter: PropTypes.func,
    clearFilters: PropTypes.func,
    changeSortBy: PropTypes.func,
    updateSuggestions: PropTypes.func,
    suggestions: PropTypes.object,
    onFetchDataRowsEndCallback: PropTypes.func, // eslint-disable-line
    notify: PropTypes.func.isRequired,
    enableClientSideSort: PropTypes.bool,
    enableClientSideFilter: PropTypes.bool,
    onRowAction: PropTypes.func,
    onRowToggleAction: PropTypes.func,
    allowExportCSV: PropTypes.bool,
    downloadData: PropTypes.object.isRequired,
    downloadActions: PropTypes.shape({
      fetchData: PropTypes.func.isRequired,
      abort: PropTypes.func.isRequired,
      reset: PropTypes.func.isRequired,
    }),
    itemsUpdated: PropTypes.number,
    itemCount: PropTypes.number,
    highlightErrorsAndWarnings: PropTypes.bool,
    minCharsForAsyncSuggestions: PropTypes.number,
    enableLocalSearch: PropTypes.bool,
    enableSavedSearches: PropTypes.bool,
    savedSearches: PropTypes.object,
    savedSearchesActions: PropTypes.shape({
      setSelectedSavedSearch: PropTypes.func.isRequired,
      setSavedSearchDirty: PropTypes.func.isRequired,
      setSavedSearchDirtyColumns: PropTypes.func.isRequired,
      fetchSavedSearches: PropTypes.func.isRequired,
      putSavedSearch: PropTypes.func.isRequired,
      postSavedSearch: PropTypes.func.isRequired,
      deleteSavedSearch: PropTypes.func.isRequired,
    }),
    alwaysRefreshOnLoad: PropTypes.bool,
    userAuthorities: PropTypes.array,
    customToolbar: PropTypes.node,
    enableOperationSelection: PropTypes.bool,
    primaryBatchSize: PropTypes.number,
    notifications: PropTypes.object,
  }

  static defaultProps = {
    isLoading: false,
    errorInfo: {},
    enableClientSideSort: false,
    enableClientSideFilter: false,
    filterClauses: [],
    addFilter: _.noop,
    removeFilter: _.noop,
    clearFilters: _.noop,
    changeSortBy: _.noop,
    updateSuggestions: _.noop,
    suggestions: {},
    onFetchDataRowsEndCallback: _.noop,
    onRowAction: _.noop,
    onRowToggleAction: _.noop,
    allowExportCSV: false,
    downloadData: {},
    downloadActions: {
      fetchData: _.noop,
      abort: _.noop,
      reset: _.noop,
    },
    itemsUpdated: 0,
    itemCount: 0,
    highlightErrorsAndWarnings: true,
    minCharsForAsyncSuggestions: 1,
    enableLocalSearch: false,
    enableSavedSearches: false,
    savedSearchesActions: {
      setSelectedSavedSearch: _.noop,
      setSavedSearchDirty: _.noop,
      setSavedSearchDirtyColumns: _.noop,
      fetchSavedSearches: _.noop,
      putSavedSearch: _.noop,
      postSavedSearch: _.noop,
      deleteSavedSearch: _.noop,
    },
    savedSearches: {},
    alwaysRefreshOnLoad: false,
    userAuthorities: [],
    customToolbar: null,
    enableOperationSelection: false,
    notifications: {},
  }

  /**
   * This is temporary so that a grid can pass in a cookies "provider" (original implementation)
   * and have that used, until all grids to use PersistentBrowserStorage instead.
   */
  getBrowserStorage = () => {
    const { cookies } = this.props
    return !_.isEmpty(cookies)
      ? new PersistentBrowserStorage({
          adapter: PersistentBrowserStorage.ADAPTERS.COOKIE,
          provider: cookies,
        })
      : new PersistentBrowserStorage({
          adapter: PersistentBrowserStorage.ADAPTERS.LOCAL_STORAGE,
        })
  }

  /**
   * This might make more sense in redux at some point, but as this is
   * pretty isolated and perhaps not necessary in redux right now.
   * We should reconsider this when we refactor datatable.
   */
  state = {
    isNonPrimaryBatchLoading: false,
    browserStorage: null,
    mounted: false,
  }

  componentDidMount() {
    this.setState({ mounted: true, browserStorage: this.getBrowserStorage() })
    const {
      rowData,
      enableSavedSearches,
      savedSearches,
      savedSearchesActions,
      alwaysRefreshOnLoad,
    } = this.props

    if (enableSavedSearches) {
      // before init, load up saved searches
      savedSearchesActions.fetchSavedSearches()
      if (!_.isEmpty(savedSearches)) this.initializeSortBy()
    } else {
      if (_.isEmpty(rowData) || alwaysRefreshOnLoad) {
        // init right away if savedSearches not enabled (no need to wait on it)
        this.initializeSortBy()
      }
    }
  }

  debouncedNotify = _.debounce((...args) => {
    this.notifyError(...args)
  })

  componentDidUpdate(prevProps) {
    const { errorInfo, enableSavedSearches, savedSearches, rowData } =
      this.props
    if (
      !_.isEmpty(errorInfo) &&
      (_.isEmpty(prevProps.errorInfo) ||
        prevProps.errorInfo.statusText !== errorInfo.statusText ||
        prevProps.rowData?.length !== rowData?.length)
    ) {
      // use debounce here because above condition can be true a couple times for same error.
      // this is in lieu of something better, like a time stamp of last query or something that tells
      // us this is a new query error
      this.debouncedNotify(errorInfo)
    }

    // wait to initialize sort by until savedSearches is filled in
    if (
      enableSavedSearches &&
      _.isEmpty(prevProps.savedSearches) &&
      !_.isEmpty(savedSearches)
    ) {
      this.initializeSortBy()
    }
  }

  notifyError = (err, debugInfo) => {
    // This is for developers, because apps can suppress information to display errors how they want, but that becomes
    // annoying when we have no idea what the initial error was.
    dataTableLogger.error(debugInfo || err)

    // notify user
    const { toast } = this.props.notifications
    toast?.open({ title: DEFAULT_ERROR_TITLE, error: err })
  }

  refreshData = async () => {
    await this.initializeSortBy()
  }

  initializeSortBy = async () => {
    const { savedSearches, changeSortBy, fetchDataRows, entityKey, columns } =
      this.props

    const dataColumns = DataTableUtils.getVisibleDataColumns(
      this.state.browserStorage,
      savedSearches,
      entityKey,
      columns
    )

    try {
      const sortValues = DataTableUtils.getSortValuesFromColumns(dataColumns)
      if (!_.isEmpty(sortValues)) {
        await changeSortBy(sortValues, columns) // This calls fetch internally
      } else {
        await fetchDataRows()
      }
      this.props.onFetchDataRowsEndCallback()
    } catch (err) {
      this.notifyError(err)
    }
  }

  onScrollEnd = _.debounce(
    async (x, y, topRowShown, lastRowShown) => {
      const primaryBatchSize = _.get(
        this,
        'props.primaryBatchSize',
        DEFAULT_PRIMARY_BATCH_SIZE
      )
      if (lastRowShown === primaryBatchSize) {
        this.setState({ isNonPrimaryBatchLoading: true })
        await this.props.fetchDataRowsAppend()
        this.setState({ isNonPrimaryBatchLoading: false })
      }
    },
    DEBOUNCE_WAIT_FOR_SCROLL,
    { leading: false, trailing: true, maxWait: DEBOUNCE_WAIT_FOR_SCROLL * 2 }
  )

  updateSuggestions = async args => {
    try {
      await this.props.updateSuggestions(args)
    } catch (err) {
      this.notifyError(err)
    }
  }

  render() {
    const {
      enableClientSideFilter,
      enableClientSideSort,
      isLoading,
      changeSortBy,
      addFilter,
      removeFilter,
      clearFilters,
      enableOperationSelection,
      ...props
    } = this.props

    const defaultProps = {
      browserStorage: this.state.browserStorage,
      refreshData: this.refreshData,
      updateSuggestions: this.updateSuggestions,
      onScrollEnd: this.onScrollEnd,
    }
    const wrapperProps = {
      ...defaultProps,
      ...props,
      isLoading,
      isNonPrimaryBatchLoading: this.state.isNonPrimaryBatchLoading,
      ...(!enableClientSideSort ? { changeSortBy } : {}),
      ...(!enableClientSideFilter
        ? { addFilter, removeFilter, clearFilters }
        : {}),
      enableOperationSelection,
    }

    return (
      this.state.mounted && ( // disallow SSR as table headers won't refresh nicely from browser storage widths
        <div
          data-elm-id='data_table_container'
          data-loading={isLoading} // This is for QA to know when grid is loading or not
          className={cx('data-table-container')}
        >
          {/* Show loader new bottom, only when loading secondary batch */}
          {isLoading && this.state.isNonPrimaryBatchLoading && (
            <Icon
              color='grayMedium'
              name='refresh'
              size='large'
              loading
              className={cx('spinner')}
            />
          )}
          <DataTable {...wrapperProps} />
        </div>
      )
    )
  }
}
export { DataTableWrapper } // for testing
export default withApplicationNotificationsContext(DataTableWrapper)
